// Time-sensitive exhibit content.
// Last reviewed: August 20, 2026
// Primary sources: Stanford HAI 2026 AI Index; IEA Energy and AI.

export const AI_EXHIBIT_CURRENT_STATUS = Object.freeze({
  lastReviewed: "August 2026",

  capabilityCards: [
    {
      id: "reasoning",
      icon: "🧠",
      shortLabel: "Reasoning benchmarks",
      headline: "Gold-level math",
      metric: "35 points",
      metricLabel: "2025 International Mathematical Olympiad",
      detail:
        "Stanford's 2026 AI Index reports that Gemini Deep Think earned gold-level performance at the 2025 International Mathematical Olympiad.",
      caution:
        "Excellent performance on a difficult benchmark does not mean the same system is equally reliable on every kind of task.",
      sourceLabel: "Stanford HAI · 2026 AI Index",
    },
    {
      id: "agents",
      icon: "🖥️",
      shortLabel: "Computer agents",
      headline: "Major progress, still imperfect",
      metric: "66.3%",
      metricLabel: "OSWorld task success",
      detail:
        "On OSWorld, which evaluates agents completing tasks across computer operating systems, top reported accuracy reached 66.3%.",
      caution:
        "That still means roughly one in three structured tasks failed on this benchmark.",
      sourceLabel: "Stanford HAI · 2026 AI Index",
    },
    {
      id: "robots",
      icon: "🤖",
      shortLabel: "Household robotics",
      headline: "The physical world is hard",
      metric: "12%",
      metricLabel: "real household task success",
      detail:
        "Stanford's 2026 AI Index reports only 12% success on real household tasks, while robotic manipulation in the RLBench simulation reached 89.4%.",
      caution:
        "Controlled environments and simulations can be dramatically easier than unfamiliar real homes.",
      sourceLabel: "Stanford HAI · 2026 AI Index",
    },
  ],

  jaggedComparisons: [
    {
      id: "math_clock",
      left: { title: "Win gold-level performance at olympiad mathematics", icon: "🥇" },
      right: { title: "Reliably read an unusual analog clock", icon: "🕰️" },
      harder: "right",
      revealTitle: "The clock can be harder.",
      reveal:
        "The 2026 AI Index reports gold-level performance at the 2025 IMO, yet the top model on ClockBench read analog clocks correctly only 50.6% of the time, versus 90.1% for humans.",
      sourceLabel: "Stanford HAI · 2026 AI Index",
    },
    {
      id: "agent_physical",
      left: { title: "Complete structured tasks on a computer", icon: "🖥️" },
      right: { title: "Handle unfamiliar chores in a real household", icon: "🏠" },
      harder: "right",
      revealTitle: "The messy physical world is much harder.",
      reveal:
        "Top reported OSWorld computer-agent task success reached 66.3%, while robots succeeded on only 12% of real household tasks in the Stanford overview.",
      sourceLabel: "Stanford HAI · 2026 AI Index",
    },
    {
      id: "simulation_real",
      left: { title: "Manipulate objects in a controlled simulation", icon: "🎮" },
      right: { title: "Manipulate objects in a real home", icon: "🧹" },
      harder: "right",
      revealTitle: "Reality adds friction, variation, and surprises.",
      reveal:
        "Stanford reports 89.4% success on RLBench robotic manipulation in simulation versus 12% on real household tasks.",
      sourceLabel: "Stanford HAI · 2026 AI Index",
    },
  ],

  environment: {
    electricity2024TWh: 460,
    electricity2030TWh: 1000,
    statement:
      "The IEA projects global electricity generation serving all data centers—not AI alone—to rise from about 460 TWh in 2024 to more than 1,000 TWh in 2030 in its base case.",
    sourceLabel: "International Energy Agency · Energy and AI",
    lastReviewed: "August 2026",
  },
});

const AI_EXHIBIT_CURRENT_STATUS_ES = Object.freeze({
  ...AI_EXHIBIT_CURRENT_STATUS,
  capabilityCards: [
    {
      ...AI_EXHIBIT_CURRENT_STATUS.capabilityCards[0],
      shortLabel: "Pruebas de razonamiento",
      headline: "Matemáticas a nivel oro",
      metricLabel: "Olimpiada Internacional de Matemáticas 2025",
      detail: "El AI Index 2026 de Stanford informa que Gemini Deep Think alcanzó rendimiento de medalla de oro en la Olimpiada Internacional de Matemáticas de 2025.",
      caution: "Un resultado excelente en una prueba difícil no significa que el mismo sistema sea igualmente fiable en todo tipo de tareas.",
    },
    {
      ...AI_EXHIBIT_CURRENT_STATUS.capabilityCards[1],
      shortLabel: "Agentes informáticos",
      headline: "Gran progreso, todavía imperfecto",
      metricLabel: "éxito de tareas en OSWorld",
      detail: "En OSWorld, que evalúa agentes realizando tareas en sistemas operativos, la mejor precisión informada llegó al 66,3%.",
      caution: "Eso todavía significa que aproximadamente una de cada tres tareas estructuradas falló en esta prueba.",
    },
    {
      ...AI_EXHIBIT_CURRENT_STATUS.capabilityCards[2],
      shortLabel: "Robótica doméstica",
      headline: "El mundo físico es difícil",
      metricLabel: "éxito en tareas domésticas reales",
      detail: "El AI Index 2026 de Stanford informa solo un 12% de éxito en tareas domésticas reales, mientras que la manipulación robótica en la simulación RLBench alcanzó 89,4%.",
      caution: "Los entornos controlados y las simulaciones pueden ser mucho más fáciles que hogares reales y desconocidos.",
    },
  ],
  jaggedComparisons: [
    {
      ...AI_EXHIBIT_CURRENT_STATUS.jaggedComparisons[0],
      left: { title: "Lograr rendimiento de oro en matemáticas olímpicas", icon: "🥇" },
      right: { title: "Leer de forma fiable un reloj analógico inusual", icon: "🕰️" },
      revealTitle: "El reloj puede ser más difícil.",
      reveal: "El AI Index 2026 informa rendimiento de oro en la IMO 2025, pero el mejor modelo en ClockBench leyó correctamente relojes analógicos solo el 50,6% de las veces, frente al 90,1% de los humanos.",
    },
    {
      ...AI_EXHIBIT_CURRENT_STATUS.jaggedComparisons[1],
      left: { title: "Completar tareas estructuradas en una computadora", icon: "🖥️" },
      right: { title: "Hacer tareas desconocidas en un hogar real", icon: "🏠" },
      revealTitle: "El mundo físico desordenado es mucho más difícil.",
      reveal: "El mejor éxito informado de agentes en OSWorld alcanzó 66,3%, mientras que los robots tuvieron solo 12% de éxito en tareas domésticas reales en el resumen de Stanford.",
    },
    {
      ...AI_EXHIBIT_CURRENT_STATUS.jaggedComparisons[2],
      left: { title: "Manipular objetos en una simulación controlada", icon: "🎮" },
      right: { title: "Manipular objetos en un hogar real", icon: "🧹" },
      revealTitle: "La realidad añade fricción, variación y sorpresas.",
      reveal: "Stanford informa 89,4% de éxito en manipulación robótica RLBench en simulación frente a 12% en tareas domésticas reales.",
    },
  ],
  environment: {
    ...AI_EXHIBIT_CURRENT_STATUS.environment,
    statement:
      "La AIE proyecta que la generación mundial de electricidad para todos los centros de datos aumente aproximadamente de 460 TWh en 2024 a más de 1.000 TWh en 2030 en su escenario base.",
    sourceLabel: "Agencia Internacional de Energía · Energy and AI",
    lastReviewed: "Agosto de 2026",
  },
});

export function getAIExhibitCurrentStatus(language = "en") {
  return language === "es" ? AI_EXHIBIT_CURRENT_STATUS_ES : AI_EXHIBIT_CURRENT_STATUS;
}
